import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "@/components/base/Select";
import Badge from "@/components/base/Badge";
import { teamMembers, drivers } from "@/mocks/team";
import type { Order, OrderTab } from "@/mocks/orders";

interface OrderTableProps {
  orders: Order[];
  tab: OrderTab;
  onAssign: (id: string, assignee: string) => void;
}

const rejectReasons = ["مشغول حالياً", "خارج النطاق", "الموعد غير متاح", "سبب آخر"];
const rejectReasonOptions = rejectReasons.map((r) => ({ value: r, label: r }));
const teamOptions = [
  { value: "", label: "اختر الفريق / الفني" },
  ...teamMembers.map((m) => ({ value: m.name, label: `${m.name} — ${m.specialty}` })),
  ...drivers.map((d) => ({ value: d.name, label: `${d.name} — مندوب توصيل` })),
];

const serviceIcon: Record<Order["serviceType"], string> = {
  "غسيل سيارات": "ri-car-line",
  "تنظيف منازل": "ri-home-4-line",
  "مغاسل ملابس": "ri-t-shirt-line",
};

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center" dir="ltr">
      {[1, 2, 3, 4, 5].map((i) => (
        <i
          key={i}
          className={`ri-star-fill text-sm ${i <= rating ? "text-[#F59E0B]" : "text-[#D5E3E3]"}`}
        />
      ))}
    </div>
  );
}

function CurrentStatusBadge({ status }: { status: string }) {
  if (status === "الفريق في الطريق") {
    return <Badge tone="teal" pulse>{status}</Badge>;
  }
  if (status === "قيد التنفيذ") {
    return <Badge tone="teal">{status}</Badge>;
  }
  if (status === "مندوب الاستلام في الطريق" || status === "قيد الغسيل") {
    return <Badge tone="blue">{status}</Badge>;
  }
  return <Badge tone="gray">{status}</Badge>;
}

function CountdownBar({ countdown, percent }: { countdown: string; percent?: number }) {
  const pct = percent ?? 50;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-[#B45309]">{countdown} متبقي</span>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[#F4F8F8]">
        <div
          className="h-full rounded-full bg-[#F59E0B] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function OrderTable({ orders, tab, onAssign }: OrderTableProps) {
  const navigate = useNavigate();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState(rejectReasons[0]);
  const [rejectOther, setRejectOther] = useState("");
  const [rejected, setRejected] = useState<string[]>([]);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState("");

  const confirmReject = (id: string) => {
    setRejected((prev) => [...prev, id]);
    setRejectingId(null);
  };

  const visible = orders.filter((o) => !rejected.includes(o.id));

  if (visible.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F0FDFD] text-ghasla-text-secondary">
          <i className="ri-inbox-line text-2xl" />
        </div>
        <p className="mt-3 text-sm text-ghasla-text-secondary">لا توجد طلبات في هذا التبويب</p>
      </div>
    );
  }

  const headerBase = "h-12 px-4 text-right text-xs font-semibold text-ghasla-text-secondary";
  const cellBase = "h-14 px-4";

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1000px] text-sm">
        <thead>
          <tr className="bg-[#F4FAFA]">
            <th className={headerBase}>رقم الطلب</th>
            <th className={headerBase}>العميل</th>
            <th className={headerBase}>الخدمة</th>
            {tab === "new" && <th className={headerBase}>التفاصيل</th>}
            {tab !== "completed" && tab !== "cancelled" && <th className={headerBase}>الموعد</th>}
            {tab === "completed" && <th className={headerBase}>التاريخ</th>}
            {tab === "cancelled" && <th className={headerBase}>تاريخ الإلغاء</th>}
            <th className={headerBase}>المبلغ</th>
            {tab === "new" && <th className={headerBase}>الوقت المتبقي</th>}
            {tab === "scheduled" && <th className={headerBase}>الحالة</th>}
            {tab === "current" && <th className={headerBase}>المنفذ</th>}
            {tab === "current" && <th className={headerBase}>الحالة</th>}
            {tab === "completed" && <th className={headerBase}>العمولة</th>}
            {tab === "completed" && <th className={headerBase}>الصافي</th>}
            {tab === "completed" && <th className={headerBase}>التقييم</th>}
            {tab === "cancelled" && <th className={headerBase}>سبب الإلغاء</th>}
            {tab === "cancelled" && <th className={headerBase}>الاسترداد</th>}
            <th className={headerBase}>إجراء</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((o) => (
            <Fragment key={o.id}>
              <tr
                onClick={() => navigate(`/provider/orders/${o.id}`)}
                className="cursor-pointer border-b border-[#F4F8F8] transition-colors last:border-0 hover:bg-[#FAFEFE]"
              >
                {/* رقم الطلب */}
                <td className={cellBase}>
                  <span className="font-medium text-ghasla-text-link">#{o.id}</span>
                </td>

                {/* العميل */}
                <td className={`${cellBase} ${tab === "new" ? "font-semibold text-ghasla-text-primary" : "text-ghasla-text-primary"}`}>
                  {o.customer}
                </td>

                {/* الخدمة */}
                <td className={cellBase}>
                  <span className="inline-flex items-center gap-1.5 text-ghasla-text-primary">
                    <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#F0FDFD] text-ghasla-text-secondary">
                      <i className={`${serviceIcon[o.serviceType]} text-sm`} />
                    </span>
                    {o.service}
                  </span>
                </td>

                {/* التفاصيل (new only) */}
                {tab === "new" && (
                  <td className={`${cellBase} text-xs text-ghasla-text-secondary`}>{o.details}</td>
                )}

                {/* الموعد / التاريخ / تاريخ الإلغاء */}
                {tab !== "completed" && tab !== "cancelled" && (
                  <td className={cellBase}>
                    {tab === "scheduled" ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-bold text-ghasla-text-heading">{o.schedule}</span>
                        {o.scheduleCountdown && (
                          <span className="text-xs text-ghasla-text-secondary">{o.scheduleCountdown}</span>
                        )}
                      </div>
                    ) : (
                      <span className="whitespace-nowrap text-ghasla-text-primary">{o.schedule}</span>
                    )}
                  </td>
                )}
                {tab === "completed" && (
                  <td className={`${cellBase} text-ghasla-text-primary`}>{o.completedDate}</td>
                )}
                {tab === "cancelled" && (
                  <td className={`${cellBase} text-ghasla-text-primary`}>{o.cancelDate}</td>
                )}

                {/* المبلغ */}
                <td className={`${cellBase} font-semibold text-ghasla-text-heading`}>
                  {o.amount} ر.س
                </td>

                {/* الوقت المتبقي (new only) */}
                {tab === "new" && o.countdown && (
                  <td className={cellBase}>
                    <CountdownBar countdown={o.countdown} percent={o.countdownPercent} />
                  </td>
                )}

                {/* الحالة (scheduled) */}
                {tab === "scheduled" && (
                  <td className={cellBase}>
                    <Badge tone={o.statusLabel === "مؤكد" ? "green" : "amber"}>
                      {o.statusLabel === "مؤكد" ? "مؤكد ✓" : "بانتظار التأكيد"}
                    </Badge>
                  </td>
                )}

                {/* المنفذ (current) */}
                {tab === "current" && (
                  <td className={`${cellBase} text-ghasla-text-primary`}>{o.assignee}</td>
                )}

                {/* الحالة (current) */}
                {tab === "current" && o.statusLabel && (
                  <td className={cellBase}>
                    <CurrentStatusBadge status={o.statusLabel} />
                  </td>
                )}

                {/* العمولة (completed) */}
                {tab === "completed" && (
                  <td className={`${cellBase} text-xs text-[#E74C3C]`}>
                    -{o.commission} ر.س
                  </td>
                )}

                {/* الصافي (completed) */}
                {tab === "completed" && (
                  <td className={`${cellBase} font-bold text-ghasla-text-heading`}>
                    {o.net} ر.س
                  </td>
                )}

                {/* التقييم (completed) */}
                {tab === "completed" && o.rating !== undefined && (
                  <td className={cellBase}>
                    <Stars rating={o.rating} />
                  </td>
                )}

                {/* سبب الإلغاء (cancelled) */}
                {tab === "cancelled" && (
                  <td className={`${cellBase} text-xs text-ghasla-text-primary`}>{o.cancelReason}</td>
                )}

                {/* الاسترداد (cancelled) */}
                {tab === "cancelled" && (
                  <td className={cellBase}>
                    <Badge tone={o.refund === "تم" ? "green" : "amber"}>
                      {o.refund === "تم" ? "تم ✓" : "جاري..."}
                    </Badge>
                  </td>
                )}

                {/* إجراء */}
                <td className={cellBase}>
                  {tab === "new" ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAssignee("");
                          setAssigningId(assigningId === o.id ? null : o.id);
                        }}
                        className="cursor-pointer whitespace-nowrap rounded-lg bg-[#27AE60] px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                      >
                        قبول وإسناد ✓
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRejectingId(rejectingId === o.id ? null : o.id);
                        }}
                        className="cursor-pointer whitespace-nowrap text-xs font-medium text-[#E74C3C] hover:underline"
                      >
                        رفض ✗
                      </button>
                    </div>
                  ) : tab === "scheduled" ? (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAssignee("");
                          setAssigningId(assigningId === o.id ? null : o.id);
                        }}
                        className="cursor-pointer text-xs font-medium text-ghasla-text-link hover:underline"
                      >
                        إسناد
                      </button>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer text-xs font-medium text-ghasla-text-secondary hover:text-ghasla-text-heading"
                      >
                        تعديل
                      </button>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer text-xs font-medium text-[#E74C3C] hover:underline"
                      >
                        إلغاء
                      </button>
                    </div>
                  ) : tab === "current" ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/provider/orders/${o.id}`);
                      }}
                      className="cursor-pointer text-xs font-medium text-ghasla-text-link hover:underline"
                    >
                      عرض التفاصيل
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/provider/orders/${o.id}`);
                      }}
                      className="cursor-pointer text-xs font-medium text-ghasla-text-link hover:underline"
                    >
                      عرض
                    </button>
                  )}
                </td>
              </tr>

              {/* Inline reject dropdown */}
              {tab === "new" && rejectingId === o.id && (
                <tr className="border-b border-[#F4F8F8] bg-[#FFF5F5]/60">
                  <td colSpan={12} className="h-auto px-4 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-medium text-ghasla-text-primary">سبب الرفض:</span>
                      <Select
                        value={rejectReason}
                        onChange={setRejectReason}
                        options={rejectReasonOptions}
                        className="w-[180px]"
                      />
                      {rejectReason === "سبب آخر" && (
                        <input
                          className="h-10 rounded-lg border border-[#E4F5F5] bg-white px-3 text-xs text-ghasla-text-heading outline-none transition-all focus:ring-2 focus:ring-[#E74C3C]/20"
                          placeholder="اكتب السبب"
                          value={rejectOther}
                          onChange={(e) => setRejectOther(e.target.value)}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => confirmReject(o.id)}
                        className="cursor-pointer rounded-lg bg-[#E74C3C] px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                      >
                        تأكيد الرفض
                      </button>
                      <button
                        type="button"
                        onClick={() => setRejectingId(null)}
                        className="cursor-pointer text-xs text-ghasla-text-secondary hover:text-ghasla-text-heading"
                      >
                        إلغاء
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Inline assign dropdown */}
              {assigningId === o.id && (
                <tr className="border-b border-[#F4F8F8] bg-[#F0FDFD]/60">
                  <td colSpan={12} className="h-auto px-4 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-medium text-ghasla-text-primary">الإسناد إلى:</span>
                      <Select
                        value={selectedAssignee}
                        onChange={setSelectedAssignee}
                        options={teamOptions}
                        className="min-w-[220px]"
                      />
                      <button
                        type="button"
                        disabled={!selectedAssignee}
                        onClick={() => {
                          onAssign(o.id, selectedAssignee);
                          setAssigningId(null);
                          setSelectedAssignee("");
                        }}
                        className="cursor-pointer whitespace-nowrap rounded-lg bg-[#00AAB7] px-4 py-2 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        تأكيد الإسناد
                      </button>
                      <button
                        type="button"
                        onClick={() => setAssigningId(null)}
                        className="cursor-pointer text-xs text-ghasla-text-secondary hover:text-ghasla-text-heading"
                      >
                        إلغاء
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}